import { FC, useMemo } from 'react';
import CostBreakdown from 'src/common/components/CostBreakdown';
import { PATHS } from 'src/common/constants';
import TermsAndCancellation from 'src/common/components/TermsAndCancellation';
import { useBookNowDirectContext } from 'src/book-now-direct/providers/BookNowDirectProvider';
import LoadingSpinner from 'src/common/components/LoadingSpinner';
import useUpdateDataToMatchCurrency from 'src/common/hooks/useUpdateDataToMatchCurrency';
import { useFormState } from 'react-final-form';
import { DEPARTURE_TIME_FIELD, type BookNowDirectFormState } from 'src/book-now-direct/constants';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import { useTranslation } from 'next-i18next';
import { defaultAmountsPlaceholders } from 'src/common/helpers';
import { filterInstabooks } from 'src/book-now-direct/helpers';
import styles from './PricingPanel.module.scss';

const PricingPanel: FC = () => {
  const { listing, loading } = useBookNowDirectContext();

  const { values } = useFormState<BookNowDirectFormState>();
  const { allInstabooksOnDate } = useBookNowDirectContext();
  const { t } = useTranslation('price-placeholders');

  /** Require defined departTime to show prices */
  const selectedInstabook = useMemo(
    () => (
      values[DEPARTURE_TIME_FIELD] ? filterInstabooks(allInstabooksOnDate, values)?.[0] : undefined
    ),
    [allInstabooksOnDate, values],
  );
  const amounts = selectedInstabook?.transaction.amounts;

  useUpdateDataToMatchCurrency(amounts?.renter_currency.code);

  const { currency } = useCurrencyContext();

  /**
   * Instead of Loading Skeletons which imply pending network activity,
   * mock an amounts shape with placeholder price_sections e.g. " Base Cost: $--.--"
   */
  const amountsWithDefaults = useMemo(() => (
    amounts?.price_sections
      ? amounts
      : defaultAmountsPlaceholders(
        currency,
        listing,
        t,
        allInstabooksOnDate[0]?.transaction.amounts,
      )
  ), [allInstabooksOnDate, amounts, currency, listing, t]);

  return (
    <div className={styles.root}>
      {amounts?.price_sections && loading && (
        <div className={styles.loading}>
          <LoadingSpinner background />
        </div>
      )}
      {amountsWithDefaults?.price_sections?.map((section) => (
        <CostBreakdown
          key={section.id}
          priceSection={section}
          renterCurrency={amountsWithDefaults.renter_currency}
          showCurrencySelector
          variant="tertiary"
        />
      ))}
      <TermsAndCancellation
        cancellationPolicy={amountsWithDefaults?.cancellation_policy}
        listingUrl={`${PATHS.LISTING}${listing.id}`}
      />
    </div>
  );
};

export default PricingPanel;
